import * as Sentry from "@sentry/react";
import React from "react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router";

// --- Types & Constants ---

export type CookieCategory = "analytics" | "personalization";

export interface CookieConsentState {
  analytics: boolean;
  personalization: boolean;
  version: number;
  timestamp: string;
}

export const COOKIE_CONSENT_STORAGE_KEY = "checkmaty_cookie_consent";
export const COOKIE_CONSENT_VERSION = 1;

export const DEFAULT_CONSENT: Omit<CookieConsentState, "version" | "timestamp"> = {
  analytics: false,
  personalization: true,
};

// --- Consent persistence helpers (synchronous, no React) ---

export function getStoredConsent(): CookieConsentState | null {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsentState;
    if (parsed.version !== COOKIE_CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function storeConsent(state: Omit<CookieConsentState, "version" | "timestamp">): void {
  const full: CookieConsentState = {
    ...state,
    version: COOKIE_CONSENT_VERSION,
    timestamp: new Date().toISOString(),
  };
  localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(full));
}

export function hasAnalyticsConsent(): boolean {
  return getStoredConsent()?.analytics === true;
}

// --- Sentry initialisation (called once, guarded) ---

let sentryInitialised = false;

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const isProduction = import.meta.env.PROD;
const sentryRelease = `checkmaty-app@${import.meta.env.VITE_APP_VERSION || "local"}`;

const replayPrivacyConfig = {
  maskAllText: false,
  blockAllMedia: false,
  block: ["[data-sentry-block]", ".sentry-block"],
  mask: [
    "[data-sentry-mask]",
    ".sentry-mask",
    'input[type="password"]',
    'input[type="email"]',
    "[contenteditable]",
  ],
};

export function initSentryIfConsented(): void {
  if (sentryInitialised || !sentryDsn) return;

  Sentry.init({
    dsn: sentryDsn,
    environment:
      import.meta.env.VITE_SENTRY_ENVIRONMENT || (isProduction ? "production" : "development"),
    release: sentryRelease,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(replayPrivacyConfig),
      Sentry.featureFlagsIntegration(),
    ],
    tracesSampleRate: isProduction ? 0.1 : 1.0,
    replaysSessionSampleRate: isProduction ? 0.01 : 0.1,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    ignoreErrors: ["ResizeObserver loop limit exceeded", "Non-Error promise rejection captured"],
  });

  sentryInitialised = true;
}

/**
 * Called at app start (main.tsx) — initialises Sentry only if user
 * has already given analytics consent in a previous session.
 */
export function maybeInitSentry(): void {
  if (hasAnalyticsConsent()) {
    initSentryIfConsented();
  }
}
